import { Controller } from "@hotwired/stimulus"

// Drives the ambient book-tutor drawer: open/close, submit a question, and keep
// the transcript scrolled. Messages themselves arrive over Turbo Streams (the
// server broadcasts both the student's turn and the tutor's reply into
// #book-tutor-messages), so this controller never renders message HTML — it
// just fires the POST and reacts to the stream via a MutationObserver.
const NUDGE_STORAGE_KEY = "book-tutor:nudge-dismissed"
const NUDGE_DELAY_MS = 1200

// Paste guard thresholds — see guardInput().
const CHUNK_CHARS = 20  // characters arriving in one input event that nobody typed
const BURST_CHARS = 5   // ...or a smaller run that arrived impossibly fast
const BURST_MS = 50
const NOTICE_MS = 4500
const PASTE_NOTICE = "Please type your question in your own words — working it out yourself is what makes it stick."
const CHUNK_NOTICE = "That looked pasted. Please type your question instead."

export default class extends Controller {
  static targets = ["panel", "launcher", "messages", "input", "thinking", "intro", "nudge", "notice"]
  static values = { sendUrl: String, blockPaste: Boolean }

  connect() {
    // The modern reader's tools rail (docs/highlights.md): its AiMee button asks
    // us to toggle, and we close when another tool (Highlights) opens, so only
    // one right-hand panel is ever open.
    this.onToolToggle = (event) => { if (event.detail?.tool === "tutor") this.toggle() }
    this.onToolOpened = (event) => { if (event.detail?.tool !== "tutor" && this.isOpen()) this.close({ silent: true }) }
    window.addEventListener("reader-tools:toggle", this.onToolToggle)
    window.addEventListener("reader-tools:opened", this.onToolOpened)
    this.hoistToBody()
    if (this.nudgeDismissed) this.element.classList.add("book-tutor--seen")
    this.observe()
    this.watchVideoLinks()
    this.guardInput()
    this.scrollToBottom()
    this.maybeShowNudge()
  }

  // The drawer is rendered inside the reader's #main, and the legacy BS2.3
  // book_reader stylesheet styles that subtree by ID — `body#book_reader #main li
  // { line-height: 33px }`, `#main p { margin: 1em 0 }`, `#main { font: 1.5em/1.5
  // Times }`, `#main img { height: auto !important }`. Two IDs beat anything our
  // class-scoped stylesheet can say, so the book's reading typography bleeds into
  // the chat bubbles. The launcher and panel are position:fixed, so hoisting the
  // root to <body> is visually a no-op and takes the whole subtree out of every
  // `#main …` selector at once — no !important arms race, and no new rule to
  // maintain the next time the reader's typography changes.
  //
  // A no-op on the BS5 openstax_reader; delete this along with the BS2.3 layout.
  hoistToBody() {
    if (this.element.parentElement === document.body) return // moving re-fires connect()
    document.body.appendChild(this.element)
  }

  disconnect() {
    this.stopFollowingViewport()
    window.removeEventListener("reader-tools:toggle", this.onToolToggle)
    window.removeEventListener("reader-tools:opened", this.onToolOpened)
    this.observer?.disconnect()
    clearTimeout(this.nudgeTimer)
    clearTimeout(this.nudgeHideTimer)
    clearTimeout(this.noticeTimer)
  }

  // ---- Paste guard ----------------------------------------------------------
  // Students were pasting homework problems straight into the drawer and getting
  // them solved. Blocking paste/drop/right-click is the obvious half; the input
  // watcher is the other, because preventDefault can't stop text that arrives by
  // some route the paste event never sees (extensions, devtools, a browser
  // clipboard path we don't know about). Any chunk too big or too fast to have
  // been typed is reverted.
  //
  // This is friction, not security — the student can still retype the question,
  // and that's deliberate: make cheating slower than just asking for help. The
  // real control is the tutor's own instructions, which refuse to hand over a
  // final answer.

  guardInput() {
    if (!this.blockPasteValue || !this.hasInputTarget) return
    const el = this.inputTarget

    el.addEventListener("paste", (e) => { e.preventDefault(); this.showNotice(PASTE_NOTICE) })
    el.addEventListener("drop", (e) => { e.preventDefault(); this.showNotice(PASTE_NOTICE) })
    el.addEventListener("contextmenu", (e) => e.preventDefault())

    this.lastValue = el.value
    this.lastInputAt = performance.now()
    el.addEventListener("input", () => {
      const now = performance.now()
      const added = el.value.length - this.lastValue.length

      if (added > CHUNK_CHARS || (added > BURST_CHARS && now - this.lastInputAt < BURST_MS)) {
        el.value = this.lastValue
        this.autogrow() // the reverted value is shorter; shrink the box back to match
        this.showNotice(CHUNK_NOTICE)
        return
      }

      this.lastValue = el.value
      this.lastInputAt = now
    })
  }

  showNotice(text) {
    if (!this.hasNoticeTarget) return
    const el = this.noticeTarget
    el.textContent = text

    // Replay the slide-in. Going hidden → shown restarts it on its own, but on a
    // repeat paste the notice is already up and the text is identical, so without
    // this the second attempt looks like nothing happened at all.
    el.style.animation = "none"
    void el.offsetWidth // force reflow so the restart takes
    el.style.animation = ""

    el.hidden = false
    clearTimeout(this.noticeTimer)
    this.noticeTimer = setTimeout(() => { this.noticeTarget.hidden = true }, NOTICE_MS)
  }

  // ---- First-visit nudge ----------------------------------------------------
  // A one-time "Hi, I'm AiMee" bubble over the launcher: without it the icon
  // alone doesn't say what it does. Dismissal is remembered in localStorage
  // (site-wide, not per chapter) so it's a greeting, not permanent chrome.

  maybeShowNudge() {
    if (!this.hasNudgeTarget || this.nudgeDismissed) return
    // Delay so it slides in as an invitation rather than being part of the page.
    this.nudgeTimer = setTimeout(() => {
      this.nudgeTarget.hidden = false
      // Next frame: [hidden] must be gone before the class flips, or there's
      // no transition to run from.
      requestAnimationFrame(() => this.element.classList.add("book-tutor--nudging"))
    }, NUDGE_DELAY_MS)
  }

  dismissNudge() {
    this.hideNudge()
  }

  hideNudge() {
    clearTimeout(this.nudgeTimer)
    if (!this.hasNudgeTarget || this.nudgeTarget.hidden) return

    this.markSeen()
    this.element.classList.remove("book-tutor--nudging")
    this.nudgeHideTimer = setTimeout(() => { this.nudgeTarget.hidden = true }, 200) // match the CSS fade
  }

  // Used once (greeting dismissed or drawer opened): no more greeting, and on
  // the modern reader's desktop the bubble itself retires to the tools rail's
  // AiMee button (CSS keys off .book-tutor--seen; docs/highlights.md).
  markSeen() {
    this.nudgeStorage("set")
    this.element.classList.add("book-tutor--seen")
  }

  get nudgeDismissed() {
    return this.nudgeStorage("get") === "1"
  }

  // localStorage throws in Safari private browsing and when cookies are blocked;
  // failing closed just means the greeting shows again, which is harmless.
  nudgeStorage(op) {
    try {
      return op === "set"
        ? window.localStorage.setItem(NUDGE_STORAGE_KEY, "1")
        : window.localStorage.getItem(NUDGE_STORAGE_KEY)
    } catch {
      return null
    }
  }

  // ---------------------------------------------------------------------------

  isOpen() {
    return this.element.classList.contains("book-tutor--open")
  }

  toggle() {
    this.isOpen() ? this.close() : this.open()
  }

  open() {
    this.hideNudge() // opening it is the strongest possible "message received"
    this.markSeen()
    this.element.classList.add("book-tutor--open")
    this.panelTarget.removeAttribute("inert")
    this.panelTarget.setAttribute("aria-hidden", "false")
    this.followViewport()
    this.scrollToBottom()
    this.inputTarget?.focus()
    window.dispatchEvent(new CustomEvent("reader-tools:opened", { detail: { tool: "tutor" } }))
  }

  // `silent`: another tool is taking the panel's place, so leave focus to it.
  close({ silent = false } = {}) {
    // Move focus out before hiding so we never leave focus inside an
    // aria-hidden/inert subtree (the console warning you saw). On the modern
    // reader the launcher can be hidden (the rail's or topbar's AiMee button
    // replaces it), so fall back to whichever of those is showing.
    if (!silent && this.panelTarget.contains(document.activeElement)) {
      const launcher = this.hasLauncherTarget && this.launcherTarget.offsetParent ? this.launcherTarget : null
      const toolsButton = [...document.querySelectorAll('[data-annotations-target="tutorButton"]')].find((b) => b.offsetParent)
      ;(launcher || toolsButton)?.focus()
    }
    this.stopFollowingViewport()
    this.element.classList.remove("book-tutor--open")
    this.panelTarget.setAttribute("aria-hidden", "true")
    this.panelTarget.setAttribute("inert", "")
    window.dispatchEvent(new CustomEvent("reader-tools:closed", { detail: { tool: "tutor" } }))
  }

  // ---- Phones: the keyboard -------------------------------------------------
  // iOS doesn't shrink a fixed, 100dvh panel when the keyboard opens; it
  // scrolls the page under it instead, pushing the header off the top and
  // leaving the chapter showing below the composer. While open on a phone,
  // size and place the panel from the visual viewport (the part above the
  // keyboard) and lock the page behind it.
  followViewport() {
    const viewport = window.visualViewport
    if (!viewport || !window.matchMedia("(max-width: 767.98px)").matches) return

    this.onViewportChange = () => {
      this.panelTarget.style.height = `${viewport.height}px`
      this.panelTarget.style.top = `${viewport.offsetTop}px`
      this.scrollToBottom()
    }
    viewport.addEventListener("resize", this.onViewportChange)
    viewport.addEventListener("scroll", this.onViewportChange)
    document.documentElement.classList.add("book-tutor-locked")
    this.onViewportChange()
  }

  stopFollowingViewport() {
    if (!this.onViewportChange) return
    window.visualViewport?.removeEventListener("resize", this.onViewportChange)
    window.visualViewport?.removeEventListener("scroll", this.onViewportChange)
    this.onViewportChange = null
    this.panelTarget.style.height = ""
    this.panelTarget.style.top = ""
    document.documentElement.classList.remove("book-tutor-locked")
  }

  // A starter chip fills the composer and stops there: the student reads the
  // prompt, can edit it, and sends on purpose — an accidental click must not
  // spend one of their capped messages. Setting .value fires no input event,
  // so the paste guard's baseline has to move by hand; otherwise the next
  // keystroke reads as a 200-character paste and the whole prompt is reverted.
  useStarter(event) {
    const el = this.inputTarget
    el.value = event.params.prompt
    this.lastValue = el.value
    this.lastInputAt = performance.now()
    this.autogrow()
    el.focus()
    el.setSelectionRange(el.value.length, el.value.length)
  }

  // Enter sends; Shift+Enter makes a newline.
  keydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.send(event)
    }
  }

  // Grow the textarea with its content, up to the CSS max-height (then it scrolls).
  autogrow() {
    const el = this.inputTarget
    el.style.height = "auto"
    el.style.height = `${el.scrollHeight}px`
  }

  send(event) {
    event.preventDefault()
    const text = this.inputTarget.value.trim()
    if (!text) return

    this.inputTarget.value = ""
    this.inputTarget.style.height = "" // reset auto-grown height back to min
    this.lastValue = "" // re-baseline the paste guard, or the sent length becomes its slack
    if (this.hasIntroTarget) this.introTarget.hidden = true
    this.showThinking()

    fetch(this.sendUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken(),
        "Accept": "application/json"
      },
      body: JSON.stringify({ message: text })
    }).catch(() => this.hideThinking()) // network error; stop the indicator
  }

  // Watch the transcript: hide the "thinking" dots once the tutor's reply lands,
  // and keep pinned to the bottom as messages stream in.
  observe() {
    if (!this.hasMessagesTarget) return
    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === 1 && node.classList?.contains("book-tutor-message--assistant")) {
            this.hideThinking()
          }
        }
      }
      this.keepThinkingLast()
      this.scrollToBottom()
      this.typesetMath()
    })
    this.observer.observe(this.messagesTarget, { childList: true })
  }

  // The indicator lives inside the transcript so it sits where the reply will
  // appear — but Turbo appends broadcast messages to that same container, which
  // lands them after it. Move it back to the end. Guarded on "already last" so
  // the move, itself a mutation, doesn't retrigger this forever.
  keepThinkingLast() {
    if (!this.hasThinkingTarget) return
    const el = this.thinkingTarget
    if (el.parentElement !== this.messagesTarget) return
    if (this.messagesTarget.lastElementChild === el) return
    this.messagesTarget.appendChild(el)
  }

  showThinking() {
    if (this.hasThinkingTarget) this.thinkingTarget.hidden = false
    this.keepThinkingLast()
    this.scrollToBottom()
  }

  hideThinking() {
    if (this.hasThinkingTarget) this.thinkingTarget.hidden = true
  }

  scrollToBottom() {
    if (this.hasMessagesTarget) this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
  }

  // ---- Video citations ------------------------------------------------------
  // The tutor cites videos as links of the form #video/<wistia id>/<m:ss>
  // (BookTutorChatService::VIDEO_CITATION_FORMAT). The embeds are on this same
  // page (the Vue video-content component), so a click jumps the on-page
  // player to that moment instead of navigating — which also keeps the play
  // attributed to the student in Wistia's stats, unlike a fresh iframe would.
  // Delegated on the transcript because replies arrive over Turbo Streams.

  watchVideoLinks() {
    if (!this.hasMessagesTarget) return
    this.messagesTarget.addEventListener("click", (event) => this.playVideoLink(event))
  }

  playVideoLink(event) {
    const link = event.target.closest?.('a[href*="#video/"]')
    if (!link) return
    const match = link.getAttribute("href").match(/#video\/([a-z0-9]+)\/(\d+):(\d{2})$/i)
    if (!match) return

    event.preventDefault()
    const [, id, minutes, seconds] = match
    // [data-videoid] is on inline players (Vue video-content, the server-rendered
    // embed). The legacy Vue popover thumbnail (<video-content-thumb>) has none,
    // so fall back to Wistia's own embed element; the player API opens a popover
    // at the requested time like any other embed.
    const embed = document.querySelector(`[data-videoid="${id}"]`) ||
      document.querySelector(`.wistia_async_${id}`)
    if (!embed) {
      this.showNotice("That video is in a different chapter.")
      return
    }

    // On phones the drawer is full-screen, so it would cover the player.
    if (window.matchMedia("(max-width: 480px)").matches) this.close()
    embed.scrollIntoView({ behavior: "smooth", block: "center" })
    this.playAt(id, Number(minutes) * 60 + Number(seconds))
  }

  // Wistia's E-v1 script exposes the on-page players; if this one hasn't
  // initialized yet, queue the request the same way the Vue component does.
  playAt(id, at) {
    const play = (video) => { video.time(at); video.play() }
    const video = window.Wistia?.api?.(id)
    if (video) return play(video)
    window._wq = window._wq || []
    window._wq.push({ id, onReady: play })
  }

  typesetMath() {
    if (window.MathJax && window.MathJax.typesetPromise) window.MathJax.typesetPromise()
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
